import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createTicket } from '../../store/slices/ticketsSlice';
import client from '../../api/client';
import { dedupeLocations } from '../../utils/locations';

const CATEGORIES = [
  { value: 'INCIDENT',     label: 'Incident' },
  { value: 'NON_INCIDENT', label: 'Non-Incident' },
];

const SUB_CATEGORIES = {
  INCIDENT:     [{ value: 'INCIDENT_SUB', label: 'Incident' }, { value: 'SERVICE_REQUEST', label: 'Service Request' }],
  NON_INCIDENT: [{ value: 'PREVENTIVE_MAINTENANCE', label: 'Preventive Maintenance' }, { value: 'QUARTERLY_REVIEW', label: 'Quarterly Review' }],
};

const FAULT_TYPES = [
  { value: 'HARDWARE', label: 'Hardware' },
  { value: 'SOFTWARE', label: 'Software' },
];

const Label = ({ children, required }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    {children}{required && <span className="text-magenta-500 ml-0.5">*</span>}
  </label>
);

const Select = ({ value, onChange, disabled, children, placeholder }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    className={`input-field ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed opacity-60' : ''}`}
  >
    <option value="">{placeholder}</option>
    {children}
  </select>
);

export default function NewTicket() {
  const dispatch = useDispatch();
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);

  const [locationId, setLocationId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('INCIDENT');
  const [faultType, setFaultType] = useState('HARDWARE');
  const [subCategory, setSubCategory] = useState('INCIDENT_SUB');
  const [isDowntime, setIsDowntime] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    client.get('/locations/?page_size=200').then(({ data }) => setLocations(dedupeLocations(data.results || data))).catch(() => {});
  }, []);

  useEffect(() => {
    setRooms([]); setRoomId(''); setDevices([]); setDeviceId(''); setSelectedDevice(null);
    if (!locationId) return;
    client.get(`/locations/${locationId}/rooms/`).then(({ data }) => setRooms(data.results || data)).catch(() => {});
  }, [locationId]);

  useEffect(() => {
    setDevices([]); setDeviceId(''); setSelectedDevice(null);
    if (!roomId) return;
    client.get('/inventory/', { params: { room: roomId, page_size: 100 } }).then(({ data }) => setDevices(data.results || data)).catch(() => {});
  }, [roomId]);

  useEffect(() => {
    setSelectedDevice(devices.find(d => String(d.id) === String(deviceId)) || null);
    setIsDowntime(false);
  }, [deviceId, devices]);

  const isPM = subCategory === 'PREVENTIVE_MAINTENANCE';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPM && !deviceId) { setError('Please select a device.'); return; }
    if (isPM && !roomId) { setError('Please select a room.'); return; }
    setLoading(true);
    setError(null);
    const payload = {
      subject,
      description,
      category,
      sub_category: subCategory,
      fault_type: faultType,
      is_downtime: isDowntime,
    };
    if (isPM) {
      payload.room = parseInt(roomId);
    } else {
      payload.device = parseInt(deviceId);
    }
    const result = await dispatch(createTicket(payload));
    setLoading(false);
    if (createTicket.fulfilled.match(result)) {
      navigate(`/tickets/${result.payload.id}`);
    } else {
      setError(result.payload || 'Failed to create ticket');
    }
  };

  const selectedLocation = locations.find(l => String(l.id) === String(locationId));

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate('/tickets')} className="text-sm text-magenta-500 hover:text-magenta-600 font-medium mb-3 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Tickets
        </button>
        <h1 className="text-2xl font-bold text-brand-500">New Ticket</h1>
        <p className="text-gray-400 text-sm mt-0.5">{isPM ? 'Create a Preventive Maintenance ticket for a room' : 'Report a new BHEL equipment incident'}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Equipment section */}
        <div className="card p-6">
          <h3 className="section-title">{isPM ? 'Location / Room' : 'Equipment'}</h3>

          {/* Category + Sub-Category at top of this card */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Category</Label>
              <Select value={category} onChange={e => {
                const cat = e.target.value;
                setCategory(cat);
                setSubCategory(SUB_CATEGORIES[cat]?.[0]?.value || '');
              }} placeholder="— Select —">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Sub-Category</Label>
              <Select value={subCategory} onChange={e => setSubCategory(e.target.value)} placeholder="— Select —">
                {(SUB_CATEGORIES[category] || []).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
            {category === 'INCIDENT' && subCategory === 'INCIDENT_SUB' && (
              <div>
                <Label required>Fault Type</Label>
                <Select value={faultType} onChange={e => setFaultType(e.target.value)} placeholder="— Select —">
                  {FAULT_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </Select>
              </div>
            )}
          </div>

          <div className={`grid gap-4 ${isPM ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <div>
              <Label required>Location</Label>
              <Select value={locationId} onChange={e => setLocationId(e.target.value)} placeholder="— Select location —">
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
              {selectedLocation && (
                <p className="text-xs text-gray-400 mt-1.5">{selectedLocation.city}{selectedLocation.state ? `, ${selectedLocation.state}` : ''} · SLA {selectedLocation.sla_class}</p>
              )}
            </div>
            <div>
              <Label required>Room</Label>
              <Select
                value={roomId}
                onChange={e => setRoomId(e.target.value)}
                disabled={!locationId || rooms.length === 0}
                placeholder={!locationId ? '— Select location first —' : rooms.length === 0 ? 'Loading...' : '— Select room —'}
              >
                {rooms.map(r => <option key={r.id} value={r.id}>{r.room_name}</option>)}
              </Select>
            </div>
            {!isPM && (
              <div>
                <Label required>Device</Label>
                <Select
                  value={deviceId}
                  onChange={e => setDeviceId(e.target.value)}
                  disabled={!roomId || devices.length === 0}
                  placeholder={!roomId ? '— Select room first —' : devices.length === 0 ? 'Loading...' : '— Select device —'}
                >
                  {devices.map(d => <option key={d.id} value={d.id}>{d.device_name}{d.serial_number ? ` — ${d.serial_number}` : ''}</option>)}
                </Select>
              </div>
            )}
          </div>

          {/* Device info card */}
          {selectedDevice && !isPM && (
            <div className="mt-4 p-4 rounded-xl text-sm" style={{background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)', border: '1px solid #D0CBF0'}}>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <p className="text-xs font-bold text-brand-400 uppercase tracking-wider mb-0.5">Serial</p>
                  <p className="font-mono font-semibold text-brand-600 text-xs">{selectedDevice.serial_number}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-brand-400 uppercase tracking-wider mb-0.5">OEM</p>
                  <p className="text-brand-700 font-medium">{selectedDevice.oem}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-brand-400 uppercase tracking-wider mb-0.5">Model</p>
                  <p className="text-brand-700 font-medium">{selectedDevice.model_part}</p>
                </div>
              </div>
              {selectedDevice?.room?.location?.spoc_name && (
                <div className="mt-3 pt-3 border-t border-brand-100">
                  <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-2">BHEL Contact</p>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-magenta-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-magenta-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-brand-600 text-sm">{selectedDevice.room.location.spoc_name}</p>
                      {selectedDevice.room.location.spoc_phone && (
                        <p className="text-xs text-gray-500 mt-0.5">📞 {selectedDevice.room.location.spoc_phone}</p>
                      )}
                      {selectedDevice.room.location.spoc_email && (
                        <p className="text-xs text-gray-500">✉ {selectedDevice.room.location.spoc_email}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Incident Details section */}
        <div className="card p-6">
          <h3 className="section-title">{isPM ? 'Maintenance Details' : 'Incident Details'}</h3>
          <div className="space-y-4">
            <div>
              <Label required>Subject</Label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Brief description of the issue"
                required
                className="input-field"
              />
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                placeholder="Detailed description of the fault — what's happening, what was tried, any error messages..."
                className="input-field resize-none"
              />
            </div>
            {/* Downtime checkbox */}
            {!isPM && selectedDevice && user.role !== 'RE' && (
              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border transition-all duration-200 hover:border-magenta-300" style={{background: 'linear-gradient(to right, #FDF0F8, #FAD9EF)', borderColor: isDowntime ? '#C1278D' : '#F5AEE0'}}>
                <input
                  type="checkbox"
                  checked={isDowntime}
                  onChange={e => setIsDowntime(e.target.checked)}
                  className="mt-0.5 rounded accent-magenta-500"
                />
                <div>
                  <p className="text-sm font-bold text-magenta-800">Mark as Downtime Ticket</p>
                  <p className="text-xs text-magenta-600 mt-0.5">
                    This device is SLA-eligible ({selectedDevice.oem} {selectedDevice.device_name}).
                    Check this if the equipment is completely unavailable — downtime clock starts from ticket creation and affects SLA calculations.
                  </p>
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Error + Actions */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
            {typeof error === 'object' ? JSON.stringify(error) : error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button type="button" onClick={() => navigate('/tickets')} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || (!isPM && !deviceId) || (isPM && !roomId) || !subject || !category}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Creating...</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>Create Ticket</>
            )}
          </button>
        </div>

      </form>
    </div>
  );
}
